import { create } from "zustand";

/**
 * Match score-reveal state — shared between the season view (`/`)
 * and the game view (`/match/$slug`).
 *
 * The legacy app rendered a "Reveal score" pill on every match card
 * with a recorded score, and the same pill on the game-view
 * matchup sidebar. Clicking either one revealed the score and the
 * REVELATION persisted across both surfaces for the duration of
 * the session — so if a viewer opened a match card, saw the score
 * was hidden, clicked Reveal, navigated into the game, the matchup
 * sidebar already showed the score (and vice-versa).
 *
 * Ephemeral by design — never persists to localStorage. A page
 * reload returns every match to "hidden" again, matching the
 * legacy `_revealedScores` Set behavior. The product intent is
 * "give the viewer a one-tap unhide for THIS session", not "remember
 * forever that this user has accepted spoilers for match X".
 *
 * Why a Zustand store and not React Context:
 *   - The season view + game view are mounted as siblings inside
 *     the router, not parent/child. A context provider would have
 *     to live on the rootRoute and dance around the existing
 *     auth/scope stores.
 *   - Reading + toggling from match cards is a hot path during
 *     filtering / sorting. Zustand's selector subscriptions keep
 *     re-renders narrow to the changed match cards only.
 */

type ScoreRevealState = {
  /** Set of match IDs whose scores the viewer has explicitly
   * revealed in this session. Lookup is O(1). */
  revealed: Set<string>;
  /** Reveal the score for a match. Idempotent. */
  reveal: (matchId: string) => void;
  /** Clear every reveal — only used on auth boundary changes
   * (e.g. sign-out) where re-rendering with stale reveals would
   * be wrong. Not exposed to viewer-facing UI. */
  clear: () => void;
};

export const useScoreRevealStore = create<ScoreRevealState>((set) => ({
  revealed: new Set<string>(),
  reveal: (matchId) =>
    set((state) => {
      if (state.revealed.has(matchId)) return state;
      const next = new Set(state.revealed);
      next.add(matchId);
      return { revealed: next };
    }),
  clear: () => set({ revealed: new Set<string>() }),
}));

/** Selector hook — `useScoreRevealed(matchId)` re-renders only
 * when THIS match's reveal state flips. */
export function useScoreRevealed(matchId: string): boolean {
  return useScoreRevealStore((s) => s.revealed.has(matchId));
}
